import { TOKEN_TYPE } from "../entities/Constants";
import { getAppNameFromPath } from "../utilities/URLUtils";
import { getEnvVar } from "../config/RuntimeConfig";

export default class SessionStorageService {
  static loginUser: { userIdKey: any; usertoken: string; expiryAt: string };

  private static getRuntimeMsalClientId(): string | null {
    try {
      const fromProgramContainer = (window as any)?.programContainer?.UiAzureRegistration;
      if (typeof fromProgramContainer === "string" && fromProgramContainer.trim()) {
        return fromProgramContainer.trim();
      }
    } catch {}

    try {
      const appId = (getAppNameFromPath() || "").toLowerCase();
      const key = appId ? `msalClientId_${appId}` : "msalClientId";
      const fromSession = sessionStorage.getItem(key);
      if (typeof fromSession === "string" && fromSession.trim()) {
        return fromSession.trim();
      }
    } catch {}

    return getEnvVar('clientId') || null;
  }

  private static getRuntimeApiScopes(): string[] {
    try {
      const fromProgramContainer = (window as any)?.programContainer?.Scopes;
      const rawScopes = typeof fromProgramContainer === "string" && fromProgramContainer.trim()
        ? fromProgramContainer
        : (getEnvVar('REACT_APP_SCOPES') || "");

      return rawScopes
        .split(/\s+/)
        .map(scope => scope.trim())
        .filter(scope =>
          !!scope &&
          !["openid", "profile", "offline_access", "email"].includes(scope.toLowerCase()) &&
          !scope.toLowerCase().startsWith("user.read")
        );
    } catch {
      return [];
    }
  }

  private static normalizeAuthToken(token?: string | null): string | null {
    if (!token) return null;

    const tokenParts = String(token)
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);

    const selectedToken = tokenParts.find(part => /^Bearer\s+/i.test(part)) || tokenParts[0] || '';
    const normalizedToken = selectedToken.replace(/^Bearer\s+/i, '').trim();
    return normalizedToken || null;
  }

  static getAccessToken(type: TOKEN_TYPE): string {
    const idTokenKey = SessionStorageService.findKey(type);
    const idTokeValue = sessionStorage.getItem(idTokenKey);

    if (idTokeValue) {
      const userInfoObject = JSON.parse(idTokeValue);
      const usertoken = userInfoObject?.secret;
      return SessionStorageService.normalizeAuthToken(usertoken);
    }

    return null;
  }

  static getUserId() {
    let userInfoString = null;
    const idTokenKey = SessionStorageService.findUserNameKey();
    if (idTokenKey) {
      userInfoString = sessionStorage.getItem(idTokenKey);
      let userInfoObject = JSON.parse(userInfoString);
      const userIdKey = userInfoObject["username"];
      return userIdKey;
    }
  }
  static getUserName() {
    let userInfoString = null;
    const idTokenKey = SessionStorageService.findUserNameKey();
    if (idTokenKey) {
      userInfoString = sessionStorage.getItem(idTokenKey);
      let userInfoObject = JSON.parse(userInfoString);
      const userIdKey = userInfoObject["name"];
      return userIdKey;
    }
  }
  static findKey(type: TOKEN_TYPE): string {
    const msalClientId = SessionStorageService.getRuntimeMsalClientId();
    if (!msalClientId) {
      return null;
    }

    const msalKey = "msal.token.keys." + msalClientId;
    const allKeyObjectString = sessionStorage.getItem(msalKey);
    const keyObject = allKeyObjectString ? JSON.parse(allKeyObjectString) : null;

    if (keyObject) {
      const accessTokenKeys = Array.from(keyObject["accessToken"]) as string[];

      if (type === TOKEN_TYPE.API) {
        const runtimeApiScopes = SessionStorageService.getRuntimeApiScopes();
        for (const scope of runtimeApiScopes) {
          const matchingTokenKey = accessTokenKeys.find((tokenKey) =>
            tokenKey.toLowerCase().includes(scope.toLowerCase())
          );
          if (matchingTokenKey) {
            return matchingTokenKey;
          }
        }
        const apiReadWrite = accessTokenKeys.find((p) =>
          p.toLowerCase().includes("api.readwrite")
        );
        if (apiReadWrite) {
          return apiReadWrite;
        }
        return (
          accessTokenKeys.find((p) => p.indexOf("api://") > -1) ||
          accessTokenKeys[0]
        );
      } else if (type === TOKEN_TYPE.GRAPH) {
        const graphTokens = accessTokenKeys.filter((p) => {
          return (
            p.indexOf("User.Read.All") > -1 ||
            p.indexOf("User.Read") > -1 ||
            p.indexOf("https://graph.microsoft.com") > -1
          );
        });
        return graphTokens[0];
      } else {
        return accessTokenKeys.filter((p) => p.indexOf("user.read") > -1)[0];
      }
    }

    return null;
  }

  static findUserNameKey(): string {
    const msalKey = "msal.account.keys";
    const allKeyObjectString = sessionStorage.getItem(msalKey);
    const keyObject = JSON.parse(allKeyObjectString);
    if (keyObject) {
      return keyObject[0];
    }

    return null;
  }

  static getPSToken(): string {
    const psTokenKey = "authTokens";
    const psTokeValue = sessionStorage.getItem(psTokenKey);
    if (psTokeValue) {
      const psTokenObject = JSON.parse(psTokeValue);
      return SessionStorageService.normalizeAuthToken(psTokenObject?.accessToken || psTokenObject?.access_token);
    }

    return null;
  }

  static getPSIDToken(): string {
    const psTokenKey = "authTokens";
    const psTokeValue = sessionStorage.getItem(psTokenKey);
    if (psTokeValue) {
      const psTokenObject = JSON.parse(psTokeValue);
      const pstoken = psTokenObject?.idToken || psTokenObject?.id_token;
      return pstoken;
    }

    return null;
  }

  // Hydrate a profile object with the persisted PS access token if it is missing.
  static hydrateProfileWithPSToken<T extends { token?: string }>(
    profile?: T | null
  ): T | null {
    const token = SessionStorageService.getPSToken();
    if (token && (!profile || !profile.token)) {
      return { ...(profile || ({} as any)), token } as T;
    }
    return profile ?? null;
  }

  /**
   * Returns true only when authTokens contains a real Public Secure (Okta) token,
   * identified by the OIDC-standard snake_case key `access_token`.
   *
   * cxp-ui's shareProfileInSession stores the CXP NextAuth session under the same
   * `authTokens` key but uses camelCase (`accessToken`).  Using this guard prevents
   * treating a CXP session as a completed PS-Okta session.
   */
  static hasPSFormatToken(): boolean {
    try {
      const raw = sessionStorage.getItem("authTokens");
      if (!raw) return false;
      const obj = JSON.parse(raw);
      return !!(obj?.access_token); // snake_case = genuine PS Okta token
    } catch {
      return false;
    }
  }

  /**
   * Returns the access token only when it originates from a real PS Okta flow
   * (snake_case `access_token`).  Ignores CXP NextAuth sessions (camelCase
   * `accessToken`) to avoid misidentifying CXP tokens as PS tokens.
   */
  static getPSFormatToken(): string | null {
    try {
      const raw = sessionStorage.getItem("authTokens");
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return SessionStorageService.normalizeAuthToken(obj?.access_token ?? null); // only real PS Okta token
    } catch {
      return null;
    }
  }

  /**
   * Like hydrateProfileWithPSToken but only hydrates from a real PS Okta token
   * (snake_case `access_token`).  Prevents the CXP NextAuth accessToken from being
   * placed in profile.token, which would make the PS auth-required checks believe
   * the user has already completed PS authentication.
   */
  static hydrateProfileWithPSFormatToken<T extends { token?: string }>(
    profile?: T | null
  ): T | null {
    const token = SessionStorageService.getPSFormatToken();
    if (token && (!profile || !profile.token)) {
      return { ...(profile || ({} as any)), token } as T;
    }
    return profile ?? null;
  }

  static getBPSToken(): string {
    const bpsTokenKey = "bps_authTokens";
    const bpsTokenValue = sessionStorage.getItem(bpsTokenKey);
    if (bpsTokenValue) {
      try {
        const bpsTokenObject = JSON.parse(bpsTokenValue);
        return SessionStorageService.normalizeAuthToken(bpsTokenObject?.access_token || null);
      } catch {
        return null;
      }
    }
    return null;
  }

  static getBPSIDToken(): string {
    const bpsTokenKey = "bps_authTokens";
    const bpsTokenValue = sessionStorage.getItem(bpsTokenKey);
    if (bpsTokenValue) {
      const bpsTokenObject = JSON.parse(bpsTokenValue);
      return bpsTokenObject?.id_token || null;
    }
    return null;
  }

  static getAuthToken(authRequired: string): string | null {
    switch (authRequired?.toLowerCase()) {
      case 'ps':
        return SessionStorageService.getPSToken();
      case 'bps':
        return SessionStorageService.getBPSToken();
      case 'ad':
        return SessionStorageService.getAccessToken(TOKEN_TYPE.API);
      default:
        return null;
    }
  }
}

export const setSessionStorageItem = (
  name: string,
  value: string,
  dispatchStorageEvent = false
) => {
  if (!name || !window?.sessionStorage) {
    return;
  }

  sessionStorage.setItem(name, value);
  if (dispatchStorageEvent) {
    const event = new CustomEvent<{ name: string; value: string }>(
      "sessionStorageChange",
      {
        detail: { name, value },
      }
    );
    window.dispatchEvent(event);
  }
};

export const getAppInsightsSessionId = (): string | null => {
  const sessionId = sessionStorage.getItem("x-session-id");
  return sessionId ? sessionId : null;
};
